// FoundHerBrands.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilter,
  faHeart,
  faStar,
  faStore,
  faFlask,
  faBoxOpen,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "../../../orderflow/hooks/useHerKart";

// Central route constants — adjust here if your router paths differ.
const ROUTES = {
  ALL_PRODUCTS: "/herkart/products",
};

// Dummy sample brands — shown only when "Sample" toggle is active
const sampleBrands = [
  {
    storeCode: "SAMPLE-MASALAMAMA",
    storeName: "Masala Mama Pickles",
    founder: "Priya Sharma",
    category: "Pickle",
    tagline: "Grandma's secret recipes, GenZ twist",
    storeHeadline: "Small-batch, chemical-free pickles made with love. Empowering women farmers in Rajasthan.",
    storeImage: "https://picsum.photos/id/1080/400/300",
    priceRange: "₹149 - ₹449",
    rating: 4.9,
    location: "Jaipur",
    isSample: true,
  },
  {
    storeCode: "SAMPLE-DREAMPLAY",
    storeName: "DreamPlay Toys",
    founder: "Neha Kapoor",
    category: "Toy",
    tagline: "Toys that spark curiosity & confidence",
    storeHeadline: "STEM toys designed especially for young girls — astronauts, coders & inventors.",
    storeImage: "https://picsum.photos/id/201/400/300",
    priceRange: "₹399 - ₹1299",
    rating: 4.8,
    location: "Bengaluru",
    isSample: true,
  },
  {
    storeCode: "SAMPLE-SWEETBLOOM",
    storeName: "SweetBloom Candies",
    founder: "Aarohi Mehra",
    category: "Candy",
    tagline: "Joy in every bite — naturally",
    storeHeadline: "Gummy bears, lollipops & fruit chews with real fruit & zero artificial junk.",
    storeImage: "https://picsum.photos/id/292/400/300",
    priceRange: "₹89 - ₹299",
    rating: 4.7,
    location: "Mumbai",
    isSample: true,
  },
  {
    storeCode: "SAMPLE-THREADBLOOM",
    storeName: "Thread & Bloom",
    founder: "Lakshmi Rao",
    category: "Handicraft",
    tagline: "Handwoven stories from rural India",
    storeHeadline: "Sustainable scarves, bags & home décor crafted by women artisans.",
    storeImage: "https://picsum.photos/id/106/400/300",
    priceRange: "₹499 - ₹2499",
    rating: 4.9,
    location: "Hyderabad",
    isSample: true,
  },
  {
    storeCode: "SAMPLE-GLOWHER",
    storeName: "GlowHer Naturals",
    founder: "Zara Khan",
    category: "Skincare",
    tagline: "Glow from within, powered by nature",
    storeHeadline: "Clean, vegan skincare made with Himalayan herbs & women-grown ingredients.",
    storeImage: "https://picsum.photos/id/669/400/300",
    priceRange: "₹299 - ₹899",
    rating: 4.8,
    location: "Dehradun",
    isSample: true,
  },
  {
    storeCode: "SAMPLE-CHAICHILL",
    storeName: "Chai & Chill",
    founder: "Meher Patel",
    category: "Wellness",
    tagline: "Functional chai that heals & energizes",
    storeHeadline: "Ayurvedic spice blends & ready-to-brew wellness teas for busy girl bosses.",
    storeImage: "https://picsum.photos/id/870/400/300",
    priceRange: "₹249 - ₹599",
    rating: 4.6,
    location: "Ahmedabad",
    isSample: true,
  },
];

export default function FoundHerBrands() {
  const navigate = useNavigate();
  const { fetchLiveStores } = useHerKart();

  const [liveStores, setLiveStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSamples, setShowSamples] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetchLiveStores();
        if (response?.success) {
          const brandStores = (response.stores || [])
            .filter((s) => s.isBrandOnly)
            .map((s) => ({ ...s, isSample: false }));
          setLiveStores(brandStores);
        }
      } catch (e) {
        console.error("Stores fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const brands = showSamples ? sampleBrands : liveStores;

  // FIX: categories were previously a hardcoded list ("Pickle", "Toy"...) that
  // only matched sampleBrands. Live stores can carry any category string, so
  // that list silently broke filtering for real data. Now derived from
  // whatever is actually present in the current brand set (live or sample).
  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(brands.map((b) => b.category).filter(Boolean))
    ).sort();
    return ["All", ...unique];
  }, [brands]);

  // Reset the filter whenever the underlying data set changes (e.g. Live <-> Sample
  // toggle) so users don't get stuck on a category that no longer exists, which
  // silently produced an empty grid before.
  useEffect(() => {
    setSelectedCategory("All");
  }, [showSamples]);

  const filteredBrands = useMemo(() => {
    return brands.filter((brand) => {
      const name = brand.storeName || "";
      const founder = brand.founder || brand.ownerName || "";
      const desc = brand.storeHeadline || "";
      const category = brand.category || "";

      const matchesSearch =
        name.toLowerCase().includes(search.toLowerCase()) ||
        founder.toLowerCase().includes(search.toLowerCase()) ||
        desc.toLowerCase().includes(search.toLowerCase());

      const matchesCategory = selectedCategory === "All" || category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [brands, search, selectedCategory]);

  const toggleFavorite = (storeCode) => {
    setFavorites((prev) =>
      prev.includes(storeCode) ? prev.filter((fid) => fid !== storeCode) : [...prev, storeCode]
    );
  };

  const handleBrandClick = (brand) => {
    if (brand.isSample) return;
    navigate(`/herkart/products/${brand.storeCode}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff0f5] via-[#f3e8ff] to-[#e0f2fe] pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-12">
        <div className="text-center mb-6">
          <div
            className="inline-flex items-center gap-3 mb-4 px-5 py-3 rounded-2xl
                bg-gradient-to-r from-pink-50 via-violet-50 to-fuchsia-50
                border border-pink-200 shadow-sm"
          >
            <span className="text-2xl lg:text-4xl">✨</span>
            <h1
              className="text-2xl lg:text-4xl font-bold tracking-tight
                 bg-gradient-to-r from-pink-600 via-violet-600 to-fuchsia-600
                 bg-clip-text text-transparent"
            >
              FoundHer Youth Brands
            </h1>
          </div>
          <p className="text-sm lg:text-lg text-violet-700 max-w-2xl mx-auto">
            Discover & promote GenZ women-founded startups that are redefining
            Indian pride with global appeal
          </p>
        </div>

        <div className="mb-2 flex flex-row justify-start gap-2 p-2 bg-purple-400 border border-purple-200 rounded-2xl">
          <button
            onClick={() => navigate(ROUTES.ALL_PRODUCTS)}
            className="flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-medium bg-pink-100 text-pink-700 hover:bg-pink-200 transition"
          >
            <FontAwesomeIcon icon={faBoxOpen} />
            Browse All HerKart Products
          </button>
          <button
            onClick={() => setShowSamples((prev) => !prev)}
            className={`flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs hover:bg-violet-700 hover: text-white font-medium ${
              showSamples ? "bg-amber-100 text-amber-700" : "bg-violet-500 text-violet-700"
            }`}
          >
            <FontAwesomeIcon icon={faFlask} />
            {showSamples ? "Showing Sample — Switch to Live" : "Showing Live — Switch to Sample"}
          </button>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-row md:items-center gap-3 md:gap-4 mb-2 p-2 bg-purple-400 border border-purple-200 rounded-2xl">
          <div className="relative w-full md:max-w-md min-w-0">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-pink-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search brands, founders, or vibes..."
              className="text-xs lg:text-sm w-full pl-12 pr-5 py-2 rounded-3xl text-black border border-pink-200 focus:border-pink-400 bg-white placeholder-violet-400 focus:outline-none"
            />
          </div>

          <div className="flex items-center gap-3 bg-white rounded-3xl px-5 py-1 border border-pink-200 w-fit shrink-0">
            <FontAwesomeIcon icon={faFilter} className="text-pink-500 shrink-0" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-transparent w-fit px-2 py-1 text-xs lg:text-sm focus:outline-none text-violet-700 font-medium cursor-pointer"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Count */}
        <p className="text-violet-600 mb-2 pl-2">
          {loading ? "Loading brands..." : `${filteredBrands.length} beautiful brands found ✨`}
        </p>

        {/* Brands Grid */}
        {loading ? (
          <div className="text-center py-20 text-violet-400">Loading brands...</div>
        ) : filteredBrands.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {filteredBrands.map((brand) => (
              <div
                key={brand.storeCode}
                onClick={() => handleBrandClick(brand)}
                className={`group bg-white rounded-3xl overflow-hidden border hover:shadow-2xl transition-all duration-300 flex flex-col ${
                  brand.isSample
                    ? "border-dashed border-amber-300 cursor-not-allowed"
                    : "border-pink-100 hover:border-violet-300 cursor-pointer"
                }`}
              >
                <div className="relative">
                  <img
                    src={brand.storeImage || "https://placehold.co/400x300/f3e8ff/9333ea?text=FoundHer"}
                    alt={brand.storeName}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(brand.storeCode);
                    }}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition"
                  >
                    <FontAwesomeIcon
                      icon={faHeart}
                      className={`text-xl ${favorites.includes(brand.storeCode) ? "text-pink-500" : "text-gray-400"}`}
                    />
                  </button>

                  {brand.isSample && (
                    <span className="absolute left-4 top-4 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-black shadow-lg">
                      Sample — Not Clickable
                    </span>
                  )}
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-2xl font-bold text-violet-950">{brand.storeName}</h3>
                      {(brand.founder || brand.ownerName) && (
                        <p className="text-pink-600 font-medium">by {brand.founder || brand.ownerName}</p>
                      )}
                    </div>
                    {brand.category && (
                      <span className="px-4 py-1 text-xs font-medium bg-pink-100 text-pink-600 rounded-full">
                        {brand.category}
                      </span>
                    )}
                  </div>

                  {brand.tagline && (
                    <p className="mt-3 text-violet-600 italic">"{brand.tagline}"</p>
                  )}
                  {brand.storeHeadline && (
                    <p className="mt-4 text-violet-700 line-clamp-3 flex-1">{brand.storeHeadline}</p>
                  )}

                  <div className="mt-6 pt-6 border-t border-pink-100 text-sm">
                    <div className="flex justify-between items-center text-violet-600">
                      <div>{brand.location || ""}</div>
                      {brand.rating && (
                        <div className="flex items-center gap-1">
                          ★ <span className="font-semibold text-amber-500">{brand.rating}</span>
                        </div>
                      )}
                    </div>
                    {brand.priceRange && (
                      <div className="text-pink-600 font-medium mt-1">{brand.priceRange}</div>
                    )}
                  </div>

                  <div className="mt-6">
                    <div className="flex-1 py-4 bg-gradient-to-r from-pink-500 to-violet-500 text-white rounded-2xl font-semibold flex items-center justify-center gap-2">
                      <FontAwesomeIcon icon={faStore} />
                      {brand.isSample ? "Sample Only" : "Visit Store"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-violet-400">
            {showSamples ? "No sample brands available." : "No live brand stores yet. Check back soon!"}
          </div>
        )}

        {/* Call to Action Banner */}
        <div className="mt-20 bg-gradient-to-r from-pink-500 to-violet-600 text-white rounded-3xl p-10 md:p-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-cyan-200">
            Want to get your brand featured?
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-lg mx-auto text-gray-200">
            Join the FoundHer community and get discovered by thousands of
            conscious buyers & collaborators.
          </p>
          <button className="px-10 py-4 bg-white text-pink-600 font-semibold rounded-2xl hover:bg-pink-50 transition text-lg">
            Connect To Your Think5Whys Digital Boutique Advisor →
          </button>
        </div>
      </div>
    </div>
  );
}